import { useMemo, useState } from 'react';
import { Info, X } from 'lucide-react';
import type { MakeupAcademyPolicySummary } from '@court-sync/shared';
import { cn } from '@/lib/utils';
import { Button, Card, CardContent } from '@/components/ui';

interface MakeupAcademyPolicyInfoProps {
  summary: MakeupAcademyPolicySummary;
  /** Visually anchor the block (e.g. calendar vs credits) */
  heading?: string;
  className?: string;
  /** Show a dismiss control; persistence requires `dismissStorageKey`. */
  dismissible?: boolean;
  /** `localStorage` key — set to `1` when dismissed (per browser, per academy key). */
  dismissStorageKey?: string;
}

export function MakeupAcademyPolicyInfo({
  summary,
  heading = 'How make-up credits work at this academy',
  className,
  dismissible = false,
  dismissStorageKey,
}: MakeupAcademyPolicyInfoProps) {
  const persistedDismissed = useMemo(() => {
    if (!dismissible || !dismissStorageKey) return false;
    try {
      return localStorage.getItem(dismissStorageKey) === '1';
    } catch {
      return false;
    }
  }, [dismissible, dismissStorageKey]);

  const [sessionDismissed, setSessionDismissed] = useState(false);
  const dismissed = persistedDismissed || sessionDismissed;

  const handleDismiss = () => {
    if (dismissStorageKey) {
      try {
        localStorage.setItem(dismissStorageKey, '1');
      } catch {
        /* ignore */
      }
    }
    setSessionDismissed(true);
  };

  if (dismissed) {
    return null;
  }

  return (
    <Card
      className={cn(
        'border-amber-200/80 bg-amber-50/50 dark:border-amber-900/50 dark:bg-amber-950/20 relative',
        className
      )}
    >
      <CardContent className="p-4 sm:p-5">
        {dismissible && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={handleDismiss}
            aria-label="Dismiss policy information"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        <div className={cn('flex gap-3', dismissible && 'pr-8')}>
          <Info
            className="w-5 h-5 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5"
            aria-hidden
          />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground mb-2">{heading}</h3>
            <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-4">
              {summary.lines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
